/**
 * Realtor Feature Routes
 *
 * The realtor nav exposes Listings / Leads / Portfolio tabs; until the
 * implementations land, this module mounts placeholder pages so the nav
 * doesn't 404. Each placeholder is replaced by its real implementation
 * in the corresponding feature module when that surface ships.
 */

import type { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { BuyerClaimPage } from "./pages/buyer-claim";
import { RealtorContactsPage } from "./pages/contacts";
import { RealtorEnquiriesPage } from "./pages/enquiries";
import { LeadDetailPage } from "./pages/lead-detail";
import { LeadFormPage } from "./pages/lead-form";
import { LeadsPipelinePage } from "./pages/leads-pipeline";
import { ListingCapturePage } from "./pages/listing-capture";
import { ListingDetailPage } from "./pages/listing-detail";
import { RealtorListingsListPage } from "./pages/listings-list";
import { ManagePortfolioPage } from "./pages/manage-portfolio";
import { RealtorMessagesPage } from "./pages/messages";
import { PublicListingPage } from "./pages/public-listing";
import { PublicPortfolioPage } from "./pages/public-portfolio";
import { RealtorSettingsPage } from "./pages/realtor-settings";
import { RealtorVerificationPage } from "./pages/realtor-verification";
import { RealtorReferralsPage } from "./pages/referrals";
import { WhatsappReviewPage } from "./pages/whatsapp-review";

export const realtorRoutes: RouteObject[] = [
	// Public listing page — UNAUTHENTICATED. Mounted at /listings/:slug
	// (no /realtor prefix) so share links read like consumer URLs.
	{
		path: "/listings/:slug",
		element: <PublicListingPage />,
	},
	// Public realtor portfolio — also unauthenticated. /r/:slug pattern
	// chosen for short, shareable URLs.
	{
		path: "/r/:slug",
		element: <PublicPortfolioPage />,
	},
	// Buyer-handoff claim landing page — UNAUTHENTICATED. Buyer arrives
	// here from the realtor's WhatsApp link, OTPs in, and gets onboarded
	// as a homeowner with a property pre-filled from the listing.
	{
		path: "/claim/:token",
		element: <BuyerClaimPage />,
	},
	{
		path: "/realtor/verification",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<RealtorVerificationPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/listings",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<RealtorListingsListPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/listings/new",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<ListingCapturePage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/listings/:id",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<ListingDetailPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/listings/:id/edit",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<ListingCapturePage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/leads",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<LeadsPipelinePage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/leads/new",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<LeadFormPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/leads/:id",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<LeadDetailPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/leads/:id/edit",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<LeadFormPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/portfolio",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<ManagePortfolioPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/referrals",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<RealtorReferralsPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/messages",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<RealtorMessagesPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/enquiries",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<RealtorEnquiriesPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/contacts",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<RealtorContactsPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/whatsapp-review",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<WhatsappReviewPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/realtor/settings",
		element: (
			<ProtectedRoute requireUserType="realtor">
				<RealtorSettingsPage />
			</ProtectedRoute>
		),
	},
];
